import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { 
  Wallet, History, Shield, User, ChevronRight, CheckCircle, 
  XCircle, ArrowDownLeft, ArrowUpRight, CreditCard, RefreshCw,
  Copy, QrCode, Send, Plus, Settings, HelpCircle, Users,
  TrendingUp, Clock, Check, X, AlertCircle, ChevronDown
} from "lucide-react";

export default function TGCashinoutPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState('wallet');
  const [showFrame, setShowFrame] = useState(true);
  const [historyFilter, setHistoryFilter] = useState('all');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const pageParam = searchParams.get('page');
    const frameParam = searchParams.get('frame');
    
    if (frameParam !== null) setShowFrame(frameParam !== 'false' && frameParam !== '0');
    
    const path = location.pathname;
    if (pageParam && ['wallet', 'history', 'verification', 'profile'].includes(pageParam)) {
      setCurrentPage(pageParam);
    } else if (path.includes('/history')) {
      setCurrentPage('history');
    } else if (path.includes('/verification')) {
      setCurrentPage('verification');
    } else if (path.includes('/profile')) {
      setCurrentPage('profile');
    } else {
      setCurrentPage('wallet');
    }
    
    if (dataParam) {
      try { setData(JSON.parse(decodeURIComponent(dataParam))); } 
      catch (e) { console.error(e); }
    }
  }, [searchParams, location.pathname]);

  useEffect(() => {
    const titles = { 
      wallet: 'Кошелёк', 
      history: 'История', 
      verification: 'Верификация',
      profile: 'Профиль'
    };
    document.title = `${titles[currentPage]} | Cashinout`;
  }, [currentPage]);

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#1c1c1e]">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const username = data.username || `@${(data.firstName || 'user').toLowerCase()}${data.visibleId || ''}`;
  const balance = parseFloat(data.balance || 0);
  const usdtBalance = parseFloat(data.usdtBalance || (balance / 90).toFixed(2));
  const btcBalance = parseFloat(data.btcBalance || (usdtBalance / 65000).toFixed(6));
  const verified = data.verified !== false;
  const fmt = (n) => parseFloat(n || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (n) => parseFloat(n || 0).toLocaleString('ru-RU');

  // Telegram Phone Frame
  const TelegramFrame = () => (
    <div className="bg-[#2c2c2e] flex-shrink-0">
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 py-2 text-white text-sm">
        <span className="font-medium">9:41</span>
        <div className="flex items-center gap-1">
          <div className="flex gap-0.5">
            {[1,2,3,4].map(i => (
              <div key={i} className="w-1 bg-white rounded-sm" style={{ height: `${8 + i * 2}px` }}></div>
            ))}
          </div>
          <span className="ml-1 text-xs">5G</span>
          <div className="ml-2 w-6 h-3 border border-white rounded-sm relative">
            <div className="absolute inset-0.5 bg-green-500 rounded-sm" style={{ width: '80%' }}></div>
          </div>
        </div>
      </div>
      {/* Telegram header */}
      <div className="flex items-center px-4 py-3 border-b border-[#3c3c3e]">
        <button className="text-blue-500 text-sm font-medium mr-4">← Назад</button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-white font-bold text-lg">C</span>
          </div>
          <div>
            <div className="text-white font-semibold">Cashinout</div>
            <div className="text-gray-400 text-xs">бот</div>
          </div>
        </div>
        <button className="text-gray-400">
          <Settings className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Bottom Navigation
  const BottomNav = () => (
    <div className="bg-[#1c1c1e] border-t border-[#3c3c3e] px-2 py-2 flex-shrink-0">
      <div className="flex justify-around">
        {[
          { id: 'wallet', icon: Wallet, label: 'Кошелёк' },
          { id: 'history', icon: History, label: 'История' },
          { id: 'verification', icon: Shield, label: 'Верификация' },
          { id: 'profile', icon: User, label: 'Профиль' },
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentPage(item.id)}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-colors ${
              currentPage === item.id ? 'text-blue-500' : 'text-gray-500'
            }`}
          >
            <item.icon className="w-6 h-6" />
            <span className="text-xs font-medium">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Wallet Page
  const WalletPage = () => (
    <div className="flex-1 overflow-auto bg-[#1c1c1e] p-4">
      {/* Total Balance Card */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-6 mb-6">
        <div className="text-white/70 text-sm mb-1">Общий баланс</div>
        <div className="text-4xl font-bold text-white mb-4">≈ ${fmt(usdtBalance)}</div>
        <div className="flex gap-3">
          <button className="flex-1 bg-white/20 hover:bg-white/30 text-white rounded-xl py-3 flex items-center justify-center gap-2 transition-colors">
            <Plus className="w-5 h-5" />
            <span className="font-medium">Пополнить</span>
          </button>
          <button className="flex-1 bg-white/20 hover:bg-white/30 text-white rounded-xl py-3 flex items-center justify-center gap-2 transition-colors">
            <Send className="w-5 h-5" />
            <span className="font-medium">Отправить</span>
          </button>
        </div>
      </div>

      {/* Crypto Balances */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-semibold">Активы</h2>
          <button className="text-blue-500 text-sm">Скрыть нулевые</button>
        </div>
        <div className="space-y-3">
          {[
            { symbol: 'USDT', name: 'Tether', balance: usdtBalance, price: 1, color: 'from-green-500 to-green-600', icon: '₮' },
            { symbol: 'RUB', name: 'Рубль', balance: balance, price: 0.011, color: 'from-blue-500 to-blue-600', icon: '₽' },
            { symbol: 'BTC', name: 'Bitcoin', balance: btcBalance, price: 65000, color: 'from-orange-500 to-orange-600', icon: '₿' },
            { symbol: 'ETH', name: 'Ethereum', balance: 0, price: 3500, color: 'from-purple-500 to-purple-600', icon: 'Ξ' },
            { symbol: 'TRX', name: 'Tron', balance: 0, price: 0.12, color: 'from-red-500 to-red-600', icon: 'T' },
          ].map((crypto, i) => (
            <div key={i} className="bg-[#2c2c2e] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${crypto.color} flex items-center justify-center text-white font-bold`}>
                  {crypto.icon}
                </div>
                <div>
                  <div className="text-white font-medium">{crypto.symbol}</div>
                  <div className="text-gray-400 text-sm">{crypto.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-white font-medium">
                  {crypto.symbol === 'RUB' ? fmtInt(crypto.balance) : fmt(crypto.balance)}
                </div>
                <div className="text-gray-400 text-sm">
                  ≈ ${fmt(crypto.balance * crypto.price)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { icon: RefreshCw, label: 'Обмен', color: 'bg-blue-500' },
          { icon: CreditCard, label: 'Карта', color: 'bg-purple-500' },
          { icon: QrCode, label: 'QR', color: 'bg-green-500' },
          { icon: Users, label: 'Реферал', color: 'bg-orange-500' },
        ].map((action, i) => (
          <button key={i} className="bg-[#2c2c2e] rounded-xl p-4 flex flex-col items-center gap-2">
            <div className={`w-10 h-10 ${action.color} rounded-full flex items-center justify-center`}>
              <action.icon className="w-5 h-5 text-white" />
            </div>
            <span className="text-gray-400 text-xs">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // History Page
  const HistoryPage = () => {
    const transactions = data.transactions || [];
    
    const filteredTx = historyFilter === 'all' 
      ? transactions 
      : transactions.filter(t => t.type === historyFilter);

    const deposits = transactions.filter(t => t.type === 'deposit');
    const withdrawals = transactions.filter(t => t.type === 'withdrawal');
    const totalDeposits = deposits.reduce((s, t) => s + parseFloat(t.amount || 0), 0);
    const totalWithdrawals = withdrawals.reduce((s, t) => s + parseFloat(t.amount || 0), 0);

    return (
      <div className="flex-1 overflow-auto bg-[#1c1c1e]">
        {/* Stats */}
        <div className="p-4 grid grid-cols-3 gap-3">
          <div className="bg-[#2c2c2e] rounded-xl p-3 text-center">
            <div className="text-gray-400 text-xs mb-1">Всего</div>
            <div className="text-white font-bold">{transactions.length}</div>
          </div>
          <div className="bg-[#2c2c2e] rounded-xl p-3 text-center">
            <div className="text-gray-400 text-xs mb-1">Пополнения</div>
            <div className="text-green-500 font-bold">+{fmtInt(totalDeposits)} ₽</div>
          </div>
          <div className="bg-[#2c2c2e] rounded-xl p-3 text-center">
            <div className="text-gray-400 text-xs mb-1">Выводы</div>
            <div className="text-red-500 font-bold">-{fmtInt(totalWithdrawals)} ₽</div>
          </div>
        </div>

        {/* Filter */}
        <div className="px-4 mb-4">
          <div className="flex bg-[#2c2c2e] rounded-xl p-1">
            {[
              { id: 'all', label: 'Все' },
              { id: 'deposit', label: 'Пополнения' },
              { id: 'withdrawal', label: 'Выводы' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setHistoryFilter(f.id)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  historyFilter === f.id 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-400'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Transaction List */}
        <div className="px-4 pb-4 space-y-2">
          {filteredTx.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <div className="text-gray-400">Нет операций</div>
            </div>
          ) : (
            filteredTx.map((tx, i) => {
              const isDeposit = tx.type === 'deposit';
              return (
                <div key={i} className="bg-[#2c2c2e] rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDeposit ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {isDeposit 
                        ? <ArrowDownLeft className="w-5 h-5 text-green-500" />
                        : <ArrowUpRight className="w-5 h-5 text-red-500" />
                      }
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {isDeposit ? 'Пополнение' : 'Вывод средств'}
                      </div>
                      <div className="text-gray-500 text-sm">{tx.date} {tx.time}</div>
                    </div>
                  </div>
                  <div className={`font-bold ${isDeposit ? 'text-green-500' : 'text-red-500'}`}>
                    {isDeposit ? '+' : '-'}{fmtInt(tx.amount)} ₽
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Verification Page
  const VerificationPage = () => (
    <div className="flex-1 overflow-auto bg-[#1c1c1e] p-4">
      {/* Status Card */}
      <div className={`rounded-2xl p-6 mb-6 ${verified ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            verified ? 'bg-green-500' : 'bg-yellow-500'
          }`}>
            {verified 
              ? <CheckCircle className="w-8 h-8 text-white" />
              : <AlertCircle className="w-8 h-8 text-white" />
            }
          </div>
          <div>
            <div className={`text-xl font-bold ${verified ? 'text-green-500' : 'text-yellow-500'}`}>
              {verified ? 'Верифицирован' : 'Не верифицирован'}
            </div>
            <div className="text-gray-400 text-sm">
              {verified ? 'Полный доступ к сервисам' : 'Ограниченные лимиты'}
            </div>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className="bg-[#2c2c2e] rounded-xl mb-6 overflow-hidden">
        <div className="p-4 border-b border-[#3c3c3e]">
          <div className="text-gray-400 text-sm mb-1">ФИО</div>
          <div className="text-white font-medium">{accountName}</div>
        </div>
        <div className="p-4 border-b border-[#3c3c3e]">
          <div className="text-gray-400 text-sm mb-1">Телефон</div>
          <div className="text-white font-medium">{data.phone || '+7 (***) ***-**-**'}</div>
        </div>
        <div className="p-4 border-b border-[#3c3c3e]">
          <div className="text-gray-400 text-sm mb-1">Email</div>
          <div className="text-white font-medium">{data.email || 'Не указан'}</div>
        </div>
        <div className="p-4">
          <div className="text-gray-400 text-sm mb-1">ID пользователя</div>
          <div className="text-white font-medium flex items-center gap-2">
            {data.visibleId || '000000'}
            <Copy className="w-4 h-4 text-gray-500 cursor-pointer hover:text-blue-500" />
          </div>
        </div>
      </div>

      {/* Verification Steps */}
      <div className="space-y-3">
        <h3 className="text-white font-semibold mb-3">Этапы верификации</h3>
        {[
          { label: 'Регистрация', done: true },
          { label: 'Подтверждение телефона', done: true },
          { label: 'Подтверждение email', done: verified },
          { label: 'Загрузка документов', done: verified },
          { label: 'Проверка личности', done: verified },
        ].map((step, i) => (
          <div key={i} className="bg-[#2c2c2e] rounded-xl p-4 flex items-center justify-between">
            <span className="text-white">{step.label}</span>
            {step.done 
              ? <Check className="w-5 h-5 text-green-500" />
              : <X className="w-5 h-5 text-gray-500" />
            }
          </div>
        ))}
      </div>

      {/* Limits */}
      <div className="mt-6 bg-[#2c2c2e] rounded-xl p-4">
        <h3 className="text-white font-semibold mb-3">Лимиты</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-400">Дневной лимит</span>
            <span className="text-white font-medium">{verified ? '500 000 ₽' : '5 000 ₽'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Месячный лимит</span>
            <span className="text-white font-medium">{verified ? 'Без ограничений' : '50 000 ₽'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Вывод на карту</span>
            <span className={verified ? 'text-green-500' : 'text-red-500'}>
              {verified ? 'Доступно' : 'Недоступно'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Profile Page
  const ProfilePage = () => (
    <div className="flex-1 overflow-auto bg-[#1c1c1e] p-4">
      {/* Avatar & Name */}
      <div className="flex flex-col items-center mb-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
          <span className="text-4xl font-bold text-white">{accountName.charAt(0)}</span>
        </div>
        <div className="text-xl font-bold text-white">{accountName}</div>
        <div className="text-gray-400">{username}</div>
        {verified && (
          <div className="flex items-center gap-1 mt-2 text-green-500 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>Верифицирован</span>
          </div>
        )}
      </div>

      {/* Balance Summary */}
      <div className="bg-[#2c2c2e] rounded-xl p-4 mb-6">
        <div className="text-gray-400 text-sm mb-2">Общий баланс</div>
        <div className="text-2xl font-bold text-white">≈ ${fmt(usdtBalance)}</div>
        <div className="text-gray-400 text-sm">≈ {fmtInt(balance)} ₽</div>
      </div>

      {/* Menu Items */}
      <div className="space-y-2">
        {[
          { icon: CreditCard, label: 'Виртуальные карты', badge: '3' },
          { icon: Users, label: 'Реферальная программа', badge: '0.3%' },
          { icon: History, label: 'История операций', badge: String((data.transactions || []).length) },
          { icon: Shield, label: 'Безопасность' },
          { icon: HelpCircle, label: 'Поддержка' },
          { icon: Settings, label: 'Настройки' },
        ].map((item, i) => (
          <button key={i} className="w-full bg-[#2c2c2e] rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5 text-gray-400" />
              <span className="text-white">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {item.badge && (
                <span className="text-gray-400 text-sm">{item.badge}</span>
              )}
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#1c1c1e] flex flex-col max-w-md mx-auto" style={{ minHeight: '100vh' }}>
      {showFrame && <TelegramFrame />}
      
      {currentPage === 'wallet' && <WalletPage />}
      {currentPage === 'history' && <HistoryPage />}
      {currentPage === 'verification' && <VerificationPage />}
      {currentPage === 'profile' && <ProfilePage />}
      
      <BottomNav />
    </div>
  );
}
